import { ReactNode, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TicketDetailModal from "@/erp/tickets-clientes/detalle-ticket/components/TicketDetailModal";

import { Contrato } from "@/shared/interfaces";
import {
  IClienteBusqueda,
  IVision360Data,
} from "@/shared/interfaces/erp/tickets-soporte-clientes";
import {
  useBuscarClientes,
  useVision360Cliente,
} from "@/shared/actions/erp/tickets-soporte-cliente";

const ACCENT = "#1565c0";

/**
 * Bloque "Visión 360 del Cliente" (read-only) para la pantalla de agendamiento
 * de VISITA. Agrupa en UN solo panel las secciones colapsables: FibraPoints,
 * valores pendientes/saldo, historial de pagos, historial de tickets.
 * Datos reusados del Visión 360 del nuevo-ticket:
 *   cédula del contrato -> useBuscarClientes -> clienteId -> useVision360Cliente.
 */
export default function AgendamientoOrdenServicioVision360({
  contrato,
}: {
  contrato?: Contrato;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({ fibrapoints: true });
  const toggle = (k: string) => setOpen((p) => ({ ...p, [k]: !p[k] }));
  // Ticket seleccionado para el modal de detalle
  const [ticketSel, setTicketSel] = useState<any | null>(null);

  const cedula: string = (contrato as any)?.cliente_data?.identificacion || "";
  const numeroReferencia: string =
    (contrato as any)?.numero_referencia || (contrato as any)?.codigo || "";

  // 1) Puente: cédula -> clienteId del micro de tickets
  const { data: buscarRes, isLoading: isLoadingBuscar } = useBuscarClientes({
    query: cedula,
    enabled: !!cedula && cedula.length >= 3,
  });
  const clientes: IClienteBusqueda[] =
    ((buscarRes?.data as any)?.data as IClienteBusqueda[]) ||
    (buscarRes?.data as any) ||
    [];

  const cliente = useMemo(() => {
    const list = Array.isArray(clientes) ? clientes : [];
    const match = list.find(
      (c) =>
        c?.contrato_codigo &&
        numeroReferencia &&
        String(c.contrato_codigo) === String(numeroReferencia)
    );
    return match || list[0] || null;
  }, [clientes, numeroReferencia]);

  // 2) Visión 360 con clienteId + contrato_id
  const { data: visionRes, isLoading: isLoadingVision } = useVision360Cliente(
    cliente?.id || "",
    cliente?.contrato_id || undefined,
    !!cliente?.id
  );
  const vision: IVision360Data | null =
    ((visionRes?.data as any)?.data as IVision360Data) ||
    (visionRes?.data as any) ||
    null;

  const fin: any = vision?.financiero || {};
  const fibraPoints: any = fin.fibra_points || {};
  const historialPoints: any[] = Array.isArray(fibraPoints.historial)
    ? fibraPoints.historial
    : [];
  const pagos: any[] = Array.isArray(fin.pagos) ? fin.pagos : [];
  const deudas: any[] = Array.isArray(fin.deudas) ? fin.deudas : [];
  const tickets: any[] = Array.isArray(vision?.tickets) ? vision.tickets : [];
  const totalPendiente: string = fin.total_pendiente ?? "0";

  const loading = isLoadingBuscar || isLoadingVision;

  // Panel contenedor (agrupa todo en una sola tarjeta con título).
  const wrap = (children: ReactNode) => (
    <Grid item xs={12} sx={{ mt: 1 }}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} color="text.primary">
            Visión 360 del Cliente
          </Typography>
        </Box>
        {children}
      </Paper>
    </Grid>
  );

  // Fila de sección colapsable compacta (header clickeable + Collapse).
  const seccion = (
    id: string,
    titulo: string,
    chip: number | string | null,
    content: ReactNode,
    ultimo = false
  ) => (
    <Box>
      <Box
        onClick={() => toggle(id)}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          py: 1,
          px: 0.5,
          cursor: "pointer",
          borderTop: "1px solid",
          borderColor: "divider",
          borderBottom: ultimo && !open[id] ? "none" : undefined,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" fontWeight={600}>
            {titulo}
          </Typography>
          {chip !== null && chip !== undefined && (
            <Chip
              size="small"
              label={chip}
              sx={{ height: 20, fontWeight: 700, bgcolor: `${ACCENT}14`, color: ACCENT }}
            />
          )}
        </Box>
        {open[id] ? (
          <ExpandLessIcon fontSize="small" color="action" />
        ) : (
          <ExpandMoreIcon fontSize="small" color="action" />
        )}
      </Box>
      <Collapse in={!!open[id]} timeout="auto" unmountOnExit>
        <Box sx={{ pt: 0.5, pb: 1.5, px: 0.5 }}>{content}</Box>
      </Collapse>
    </Box>
  );

  if (!cedula) {
    return wrap(
      <Alert severity="info" variant="outlined">
        Sin identificación del cliente para consultar el Visión 360.
      </Alert>
    );
  }

  if (loading && !vision) {
    return wrap(
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!cliente || !vision) {
    return wrap(
      <Alert severity="warning" variant="outlined">
        No se encontró Visión 360 para el cliente (cédula {cedula}).
      </Alert>
    );
  }

  return wrap(
    <>
      {/* FibraPoints */}
      {seccion(
        "fibrapoints",
        "FibraPoints",
        fibraPoints.puntos_disponibles ?? 0,
        <>
          <Typography variant="caption" color="text.secondary">
            Puntos disponibles
          </Typography>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: ACCENT }}>
            {fibraPoints.puntos_disponibles ?? "-"}
          </Typography>
          {historialPoints.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Causal</TableCell>
                    <TableCell align="right">Canjeados</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Fecha</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historialPoints.map((h: any, i: number) => (
                    <TableRow key={h.id ?? i}>
                      <TableCell>{h.nombre_causal || "-"}</TableCell>
                      <TableCell align="right">{h.puntos_canjeados ?? "-"}</TableCell>
                      <TableCell align="right">${h.valor_real ?? "-"}</TableCell>
                      <TableCell>
                        <Chip
                          label={h.status || "-"}
                          size="small"
                          variant="outlined"
                          color={h.status === "PROCESADO" ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell>{h.created_at || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sin canjes registrados.
            </Typography>
          )}
        </>
      )}

      {/* Valores pendientes / Saldo */}
      {seccion(
        "saldo",
        "Valores pendientes / Saldo",
        `$${totalPendiente}`,
        <>
          <Grid container spacing={3} sx={{ mb: 1 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">
                Total Pendiente
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: parseFloat(totalPendiente) > 0 ? "#d32f2f" : "#4caf50" }}
              >
                ${totalPendiente}
              </Typography>
            </Grid>
            {fin.saldo !== undefined && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Saldo (Softnet)
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ color: parseFloat(`${fin.saldo}`) > 0 ? "#d32f2f" : "#4caf50" }}
                >
                  ${fin.saldo}
                </Typography>
              </Grid>
            )}
          </Grid>
          {deudas.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Período</TableCell>
                    <TableCell>Concepto</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                    <TableCell>Vencimiento</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deudas.map((d: any, i: number) => (
                    <TableRow key={d.idValorGenerado || i}>
                      <TableCell>{d.idMes && d.anio ? `${d.idMes}/${d.anio}` : "-"}</TableCell>
                      <TableCell>{d.observaciones || "-"}</TableCell>
                      <TableCell align="right">${d.totalAPagar ?? "-"}</TableCell>
                      <TableCell align="right" sx={{ color: "error.main", fontWeight: 600 }}>
                        ${d.valorSaldo ?? "-"}
                      </TableCell>
                      <TableCell>{d.fechaVencimiento || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="success.main">
              Sin deudas pendientes.
            </Typography>
          )}
        </>
      )}

      {/* Historial de pagos */}
      {seccion(
        "pagos",
        "Historial de pagos",
        pagos.length,
        pagos.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Período</TableCell>
                  <TableCell>Concepto</TableCell>
                  <TableCell align="right">A pagar</TableCell>
                  <TableCell align="right">Pagado</TableCell>
                  <TableCell>Documento</TableCell>
                  <TableCell>Fecha pago</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagos.map((p: any, i: number) => (
                  <TableRow key={p.idValorGenerado || i}>
                    <TableCell>{p.idMes && p.anio ? `${p.idMes}/${p.anio}` : "-"}</TableCell>
                    <TableCell>{p.observaciones || "-"}</TableCell>
                    <TableCell align="right">${p.totalAPagar ?? "-"}</TableCell>
                    <TableCell align="right">${p.totalPagado ?? "-"}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {p.numeroDocumento || "-"}
                    </TableCell>
                    <TableCell>{p.fechaPago || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Sin pagos registrados.
          </Typography>
        )
      )}

      {/* Historial de tickets */}
      {seccion(
        "tickets",
        "Historial de tickets",
        tickets.length,
        tickets.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Prioridad</TableCell>
                  <TableCell>Área</TableCell>
                  <TableCell>Fecha</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map((t: any, i: number) => (
                  <TableRow key={t.id_ticket || i}>
                    <TableCell
                      onClick={() => setTicketSel(t)}
                      sx={{
                        fontFamily: "monospace",
                        color: ACCENT,
                        fontWeight: 600,
                        cursor: "pointer",
                        textDecoration: "underline",
                        "&:hover": { opacity: 0.75 },
                      }}
                    >
                      {t.codigo_ticket || "-"}
                    </TableCell>
                    <TableCell>{t.tipo || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        label={t.estado || "-"}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: t.estado_color || undefined,
                          color: t.estado_color || undefined,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t.prioridad || "-"}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: t.prioridad_color || undefined,
                          color: t.prioridad_color || undefined,
                        }}
                      />
                    </TableCell>
                    <TableCell>{t.area_destino || "-"}</TableCell>
                    <TableCell>{t.fecha_creacion || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Sin tickets registrados.
          </Typography>
        ),
        true
      )}

      {/* Modal con el detalle COMPLETO del ticket (reusa el del módulo de tickets) */}
      <TicketDetailModal
        ticketId={ticketSel?.id_ticket ?? null}
        open={!!ticketSel}
        onClose={() => setTicketSel(null)}
        readOnly
      />
    </>
  );
}
